import { sha256Hex } from './sha256';
import { getEnvValue } from './env-config';
import { decryptKey } from './api-key-crypto';
import { getServerTimestamp } from './server-time';
import { getUrlParamFromUrl } from './url-param';

/**
 * 前後端介面簽名校驗（對應後端 com.qcommerce.util.ApiSignatureUtil）。
 *
 * 簽名規則（後端交付文檔「API 請求簽名認證 前端串接說明」第 2 節）：
 *   X-Signature = SHA-256( UTF-8(apiKey + "\n" + timestamp + "\n" + nonce) )
 * 輸出 64 位十六進位小寫；不是 Base64，也不是 HMAC；
 * HTTP 方法、URL 與請求體都不參與簽名。
 * 每次請求附帶四個請求頭：
 *   X-Api-Key  雙方約定的共享密鑰（必須與後端 API_SIGNATURE_KEY 一致）
 *   X-Timestamp Unix 秒級整數，取後端回應裡的 serverTime（見 src/utils/server-time.js）；
 *               後端預設只接受前後 300 秒內的請求，前端自己生成的時間可能被拒
 *   X-Nonce    URL-safe 隨機字串（A-Z a-z 0-9 _ -，最長 128），後端寫入 Redis 防重放
 *   X-Signature SHA-256 摘要
 *
 * :warning: 依後端文檔第 1、5 節，瀏覽器不應持有共享密鑰，簽名應由 BFF / 網關完成
 * （見 bff/ 與 README）。本檔案的「瀏覽器自行簽名」實作只是 BFF 尚未就緒時的應急方案，
 * 且必須由部署方在執行時明確配置 API_SIGNATURE_KEY 才會生效；未配置時完全不影響請求。
 * 任何重試都必須重新生成整套 headers（含新的 nonce），不能重放舊的簽名。
 */

/** 認證密鑰請求頭 */
export const SIGNATURE_HEADER_API_KEY = 'X-Api-Key';
/** 時間戳請求頭（秒） */
export const SIGNATURE_HEADER_TIMESTAMP = 'X-Timestamp';
/** 隨機數請求頭（防重放） */
export const SIGNATURE_HEADER_NONCE = 'X-Nonce';
/** 簽名請求頭 */
export const SIGNATURE_HEADER_SIGNATURE = 'X-Signature';

/** sessionStorage 中存放 apiKey 的 key（由 url 帶入時暫存，只保留在當前分頁） */
export const API_KEY_STORAGE_KEY = 'jb-api-signature-key';

/** url 上可能承載 apiKey 的引數名，按優先順序匹配 */
const URL_API_KEY_KEYS = ['apiKey', 'api-key', 'apikey', 'api-signature-key', 'x-api-key'];

/** nonce 允許的字元集與長度：A-Z a-z 0-9 _ -，最長 128 字元（見文檔第 3 節） */
export const NONCE_PATTERN = /^[A-Za-z0-9_-]{1,128}$/;

/** 明確開啟簽名校驗的字串值 */
const ENABLE_VALUES = ['1', 'true', 'yes', 'on'];

function getStorage() {
  try {
    // 隱私模式或非瀏覽器環境下訪問 sessionStorage 可能直接拋錯
    return window.sessionStorage;
  } catch (e) {
    return null;
  }
}

/** 從 url 上取出 apiKey */
export function getApiKeyFromUrl(url) {
  return getUrlParamFromUrl(url, URL_API_KEY_KEYS);
}

/**
 * 進入應用 / 頁面時呼叫：路由(url)上帶有 apiKey 才寫入 sessionStorage。
 * 沒有 apiKey 時不執行任何操作（不寫入、不清除），直接返回 null。
 * @param {String} [url] 目標 url，預設取當前位址欄
 * @returns {String|null} 有 apiKey 時返回寫入的值，沒有時返回 null
 */
export function saveApiKeyFromUrl(url) {
  const urlApiKey = getApiKeyFromUrl(url);
  if (!urlApiKey) {
    return null;
  }
  const storage = getStorage();
  if (!storage) {
    return null;
  }
  storage.setItem(API_KEY_STORAGE_KEY, urlApiKey);
  return urlApiKey;
}

/** 清除暫存的 apiKey */
export function removeApiKey() {
  const storage = getStorage();
  if (storage) {
    storage.removeItem(API_KEY_STORAGE_KEY);
  }
}

/**
 * 讀取 apiKey，優先順序：
 *   1. 執行時設定 window.ENV_CONFIG.API_SIGNATURE_KEY
 *   2. 構建時明文 VUE_APP_API_SIGNATURE_KEY（應急後門，通常留空）
 *   3. 構建時密文 VUE_APP_API_SIGNATURE_KEY_ENC（enc: 開頭，在這裡解密成明文使用）
 *   4. url / sessionStorage 帶入（見 saveApiKeyFromUrl）
 * 倉庫與 dist/ 裡只有密文；解密失敗時只印出原因（不含密鑰或密文內容），
 * 並繼續往下找，不會讓整個應用掛掉。
 * @returns {String|null} 未配置時返回 null
 */
export function getApiKey() {
  const configured = getEnvValue('API_SIGNATURE_KEY', 'VUE_APP_API_SIGNATURE_KEY');
  if (configured) {
    return configured;
  }

  const encrypted = getEnvValue('API_SIGNATURE_KEY_ENC', 'VUE_APP_API_SIGNATURE_KEY_ENC');
  if (encrypted) {
    try {
      return decryptKey(encrypted);
    } catch (error) {
      // 只記原因，不記任何密鑰 / 密文內容
      console.warn('[api-signature] 無法解開構建時配置的密文密鑰，本次不附簽名請求頭：', error.message);
    }
  }

  const storage = getStorage();
  const saved = storage ? storage.getItem(API_KEY_STORAGE_KEY) : null;
  return saved && saved.trim() ? saved.trim() : null;
}

/**
 * 是否啟用簽名校驗。
 * 明確配置 API_SIGNATURE_ENABLE 時以其為準（0/false 可臨時關閉），
 * 未配置時只要有 apiKey 就啟用，沒配置 apiKey 則完全不影響原有請求。
 */
export function isSignatureEnabled() {
  const flag = getEnvValue('API_SIGNATURE_ENABLE', 'VUE_APP_API_SIGNATURE_ENABLE');
  if (flag !== '') {
    return ENABLE_VALUES.indexOf(flag.toLowerCase()) !== -1;
  }
  return !!getApiKey();
}

/**
 * 當前 Unix 時間戳（秒）；注意不是 JavaScript 的毫秒時間戳。
 * 後端要求時間戳取伺服器返回的 serverTime，所以這裡取校準後的值：
 * 已從回應信封拿到 serverTime 時是伺服器時間，還沒拿到時才退回本機時間。
 */
export function createTimestamp() {
  return getServerTimestamp();
}

/** timestamp 必須是 Unix 秒級整數（毫秒時間戳會被後端判為非法） */
export function isValidTimestamp(timestamp) {
  return /^\d{1,10}$/.test(String(timestamp));
}

/** nonce 必須只含 A-Z a-z 0-9 _ - 且不超過 128 字元 */
export function isValidNonce(nonce) {
  return NONCE_PATTERN.test(String(nonce));
}

/** 生成 16 位元組隨機 nonce（32 位十六進位字元） */
export function createNonce() {
  const cryptoObj = typeof window !== 'undefined' ? window.crypto : null;
  if (cryptoObj && typeof cryptoObj.getRandomValues === 'function') {
    const bytes = new Uint8Array(16);
    cryptoObj.getRandomValues(bytes);
    let nonce = '';
    for (let i = 0; i < bytes.length; i++) {
      nonce += ('0' + bytes[i].toString(16)).slice(-2);
    }
    return nonce;
  }

  // 後備方案：crypto 不可用時用時間戳加隨機數拼出唯一值
  let nonce = Date.now().toString(16);
  while (nonce.length < 32) {
    nonce += ('0' + Math.floor(Math.random() * 256).toString(16)).slice(-2);
  }
  return nonce.slice(0, 32);
}

/** 拼接簽名原文：apiKey + "\n" + timestamp + "\n" + nonce */
export function buildSignatureContent(apiKey, timestamp, nonce) {
  return apiKey + '\n' + timestamp + '\n' + nonce;
}

/** 計算簽名（十六進位小寫） */
export function createSignature(apiKey, timestamp, nonce) {
  return sha256Hex(buildSignatureContent(apiKey, timestamp, nonce));
}

/**
 * 產生四個簽名請求頭
 * @param {Object} [options]
 * @param {String} [options.apiKey] 指定密鑰，預設取 getApiKey()
 * @param {Number|String} [options.timestamp] 指定時間戳，預設取當前時間
 * @param {String} [options.nonce] 指定 nonce，預設隨機生成
 * @returns {Object|null} 沒有 apiKey 時返回 null，呼叫方據此跳過簽名
 */
export function createSignatureHeaders(options) {
  const opts = options || {};
  const apiKey = opts.apiKey ? String(opts.apiKey).trim() : getApiKey();
  if (!apiKey) {
    return null;
  }

  const timestamp = opts.timestamp === undefined || opts.timestamp === null
    ? createTimestamp()
    : opts.timestamp;
  const nonce = opts.nonce ? String(opts.nonce) : createNonce();

  // 呼叫方明確指定時先校驗，避免送出後端一定拒絕的簽名
  if (!isValidTimestamp(timestamp)) {
    throw new Error('X-Timestamp 格式非法：必須是 Unix 秒級整數');
  }
  if (!isValidNonce(nonce)) {
    throw new Error('X-Nonce 格式非法：僅允許 A-Z a-z 0-9 _ -，最長 128 字元');
  }

  const headers = {};
  headers[SIGNATURE_HEADER_API_KEY] = apiKey;
  headers[SIGNATURE_HEADER_TIMESTAMP] = String(timestamp);
  headers[SIGNATURE_HEADER_NONCE] = nonce;
  headers[SIGNATURE_HEADER_SIGNATURE] = createSignature(apiKey, timestamp, nonce);
  return headers;
}

/**
 * 把簽名請求頭附加到請求設定上。
 * 這裡會複製一份 headers，避免多個請求共用同一個 headers 物件時互相覆蓋簽名。
 * @param {Object} config axios 請求設定
 * @param {Object} [options] 透傳給 createSignatureHeaders
 * @returns {Object} 附加簽名後的請求設定；取不到 apiKey 時原樣返回
 */
export function applySignatureHeaders(config, options) {
  const headers = createSignatureHeaders(options);
  if (!headers) {
    return config;
  }

  const merged = Object.assign({}, config);
  merged.headers = Object.assign({}, config.headers, headers);
  return merged;
}
